// ABOUTME: Renders plain markdown text responses and parse error displays.
// ABOUTME: Used by ContentBlockRenderer and AgentExecutionView for text and error content.

import { motion } from 'framer-motion';
import { AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export function ParseErrorDisplay({
  errorMessage,
  rawContent,
  details,
}: {
  errorMessage: string;
  rawContent: string;
  details?: Record<string, unknown>;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-sm bg-error-light border border-error/20 overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-error/20 flex items-center gap-2">
        <AlertCircle className="w-4 h-4 text-error" />
        <span className="text-sm font-medium text-error">
          Response Parse Error
        </span>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-sm text-surface-600">{errorMessage}</p>

        {details && Object.keys(details).length > 0 && (
          <div className="text-xs">
            <div className="text-surface-500 mb-1">Details:</div>
            <pre className="bg-surface-100 rounded-sm p-2 overflow-x-auto text-surface-600">
              {JSON.stringify(details, null, 2)}
            </pre>
          </div>
        )}

        <div className="text-xs">
          <div className="text-surface-500 mb-1">Raw content (truncated):</div>
          <pre className="bg-surface-100 rounded-sm p-2 overflow-x-auto text-surface-600 max-h-32">
            {rawContent.length > 500 ? rawContent.slice(0, 500) + '...' : rawContent}
          </pre>
        </div>
      </div>
    </motion.div>
  );
}

export function TextResponseDisplay({ content, inline = false }: { content: string; inline?: boolean }) {
  const markdownContent = (
    <div className="prose prose-sm max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => (
            <p className="text-sm text-surface-600 leading-relaxed mb-3 last:mb-0">
              {children}
            </p>
          ),
          strong: ({ children }) => (
            <strong className="text-surface-800 font-semibold">{children}</strong>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside space-y-1 text-surface-600 mb-3">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside space-y-1 text-surface-600 mb-3">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="text-surface-600">{children}</li>,
          code: ({ children }) => (
            <code className="px-1.5 py-0.5 rounded-sm bg-surface-100 text-primary-600 font-mono text-xs">
              {children}
            </code>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );

  // When inline, render without wrapper (for use inside AgentExecutionView card)
  if (inline) {
    return markdownContent;
  }

  // Standalone use - wrap in a card
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="card rounded-sm p-4"
    >
      {markdownContent}
    </motion.div>
  );
}
